/**
 * Rechenkern parseTaskQuickEntry (Story 23.14, FR-163, EXPERIENCE §4.22, AD-27) – rein: keine Datenbank, keine Uhr, kein
 * Netz. Liest einen Satz der Schnell-Erfassung („morgen Kunde 20105 anrufen dringend @Anna“) und erkennt Fälligkeit,
 * Person, Priorität, Labels, Projekt und Datensatz. `today` (Kalendertag Europe/Berlin) und das Verzeichnis (Personen,
 * Labels, aktive Projekte) kommen als Eingabe. Ergebnis trägt `TASK_QUICK_ENTRY_VERSION`.
 *
 * Regeln:
 * - Erkennungswörter und Schreibweisen nur hier in `TASK_QUICK_ENTRY_WORDS` (Oberfläche liest Platzhalter und Hilfe daraus).
 * - Erstes Datum, erste Priorität, erste Person, erstes Projekt und erster Datensatz gewinnen; weitere bleiben Titeltext.
 * - Wochentag-Kürzel (Mo … So) nur großgeschrieben, ausgeschriebene Namen beliebig; immer der nächste solche Tag nach
 *   heute (am Montag „Mo“ = nächster Montag). „nächste Woche“ = Montag der nächsten Woche. `TT.MM.` vor heute = nächstes
 *   Jahr; ungültige Daten (31.02.) werden nicht erkannt.
 * - Person: exakter Vorname oder Kürzel, sonst Vorname-Präfix; mehr als ein Treffer = mehrdeutig (nie still aufgelöst).
 *   Projekt: Wort-Präfix der übergebenen (aktiven) Projekte, mehr als einer = mehrdeutig. Label: Name genau (ohne
 *   Leerzeichen, Groß-/Kleinschreibung egal). Unbekanntes `@`/`#`/`+` bleibt Titeltext (nie neu angelegt).
 * - Datensatz-Wörter („Kunde 20105“) bleiben im Titel; nur markiert. `options.records: false` (Rolle Mitarbeiter) erkennt keinen.
 * - `options.ignore` nimmt eine Erkennung zurück (Chip-×): ganze Art oder ein bestimmtes Wort; das Wort bleibt Titeltext.
 */
import { isIsoDate } from '@till-erp/platform/dates';

export const TASK_QUICK_ENTRY_VERSION = 'task-quick-entry-v1';

/** Erkennungswörter der Schnell-Erfassung – einzige Quelle (Kern, Platzhalter, Hilfe). */
export const TASK_QUICK_ENTRY_WORDS = {
  /** Tage ab heute; Groß-/Kleinschreibung egal. */
  relativeDays: { heute: 0, morgen: 1, übermorgen: 2 } as Readonly<Record<string, number>>,
  /** „in 3 Tagen“ / „in 1 Tag“. */
  inDays: { prefix: 'in', units: ['tag', 'tagen'] as readonly string[], example: 'in 3 Tagen' },
  /** Montag der nächsten Woche. */
  nextWeek: ['nächste', 'woche'] as readonly string[],
  nextWeekText: 'nächste Woche',
  /** Kürzel nur so geschrieben (Index 0 = Montag). */
  weekdaysShort: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as readonly string[],
  /** Ausgeschriebene Namen, Groß-/Kleinschreibung egal (Index 0 = Montag). */
  weekdaysLong: ['montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag', 'sonntag'] as readonly string[],
  /** Zahlen-Datum: `TT.MM.` oder `TT.MM.JJJJ` (auch einstellig). */
  dateExample: '26.02.',
  /** Priorität: Wort → Stufe; Groß-/Kleinschreibung egal. */
  priorities: { dringend: 'urgent', '!!': 'urgent', hoch: 'high', niedrig: 'low' } as Readonly<Record<string, QuickEntryPriority>>,
  person: '@',
  label: '#',
  project: '+',
  /** Datensatz: Wort davor → Art (Groß-/Kleinschreibung egal). */
  records: { kunde: 'customer', auftrag: 'order', artikel: 'article' } as Readonly<Record<string, QuickEntryRecordKind>>,
} as const;

export type QuickEntryPriority = 'urgent' | 'high' | 'normal' | 'low';
export type QuickEntryRecordKind = 'customer' | 'order' | 'article';
export type QuickEntryKind = 'due' | 'priority' | 'person' | 'label' | 'project' | 'record';

export interface QuickEntryPerson {
  id: string;
  displayName: string;
  /** Kürzel, z. B. „AT“. */
  initials: string;
}

export interface QuickEntryNamed {
  id: string;
  name: string;
}

export interface QuickEntryDirectory {
  people: readonly QuickEntryPerson[];
  labels: readonly QuickEntryNamed[];
  /** Nur aktive Projekte (Auswahl des Aufrufers). */
  projects: readonly QuickEntryNamed[];
}

/** Erkennung zurücknehmen: ganze Art oder genau ein Wort dieser Art (z. B. `{ kind: 'label', text: '#Messe' }`). */
export type QuickEntryIgnore = QuickEntryKind | { kind: QuickEntryKind; text: string };

export interface QuickEntryOptions {
  ignore?: readonly QuickEntryIgnore[];
  /** `false` = keinen Datensatz erkennen (Rolle Mitarbeiter). Vorgabe `true`. */
  records?: boolean;
}

export interface QuickEntryToken {
  kind: QuickEntryKind;
  /** Zeichen-Position im Text (`end` exklusiv). */
  start: number;
  end: number;
  text: string;
}

export type QuickEntryPick<T> = T | { ambiguous: T[]; text: string };

export interface QuickEntryResult {
  version: string;
  /** Titel ohne erkannte Wörter (Datensatz-Wörter bleiben); leer = „Titel fehlt“. */
  title: string;
  /** `null` = kein Datum erkannt (Aufrufer: heute). */
  dueDate: string | null;
  priority: QuickEntryPriority | null;
  person: QuickEntryPick<QuickEntryPerson> | null;
  labels: QuickEntryNamed[];
  project: QuickEntryPick<QuickEntryNamed> | null;
  record: { kind: QuickEntryRecordKind; number: string } | null;
  tokens: QuickEntryToken[];
}

/** Ist eine Auswahl mehrdeutig (Chip „… wählen“)? */
export function isAmbiguous<T>(pick: QuickEntryPick<T> | null): pick is { ambiguous: T[]; text: string } {
  return pick !== null && typeof pick === 'object' && 'ambiguous' in pick;
}

interface Word {
  text: string;
  start: number;
  end: number;
}

const DAY_MS = 86_400_000;

function dayNumber(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d) / DAY_MS;
}

function isoOf(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

/** Wochentag 0 = Montag … 6 = Sonntag. */
function weekdayOf(day: number): number {
  return (new Date(day * DAY_MS).getUTCDay() + 6) % 7;
}

const NUMERIC_DATE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})?$/;
const CUSTOMER_OR_ORDER_NUMBER = /^\d+$/;
const ARTICLE_NUMBER = /^[A-Za-z0-9][A-Za-z0-9._/-]*$/;

const pad = (n: number) => String(n).padStart(2, '0');

function numericDate(text: string, today: number): number | null {
  const m = NUMERIC_DATE.exec(text);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  if (m[3] !== undefined) {
    const iso = `${m[3]}-${pad(month)}-${pad(day)}`;
    return isIsoDate(iso) ? dayNumber(iso) : null;
  }
  // Ohne Jahr: dieses Jahr, liegt der Tag vor heute (oder gibt es ihn dieses Jahr nicht, 29.02.), das nächste.
  const year = new Date(today * DAY_MS).getUTCFullYear();
  for (const y of [year, year + 1]) {
    const iso = `${y}-${pad(month)}-${pad(day)}`;
    if (isIsoDate(iso) && dayNumber(iso) >= today) return dayNumber(iso);
  }
  return null;
}

/** Nur eigene Einträge der Wörterliste: „constructor“ oder „__proto__“ sind Titeltext, keine geerbten Eigenschaften. */
function own<T>(table: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.hasOwn(table, key) ? table[key] : undefined;
}

/** Datum ab Wort `i`; Ergebnis = Tag und Anzahl Wörter. */
function dateAt(words: readonly Word[], i: number, today: number): { day: number; count: number } | null {
  const W = TASK_QUICK_ENTRY_WORDS;
  const word = words[i]!.text;
  const lower = word.toLowerCase();
  const rel = own(W.relativeDays, lower);
  if (rel !== undefined) return { day: today + rel, count: 1 };
  const short = W.weekdaysShort.indexOf(word);
  const long = W.weekdaysLong.indexOf(lower);
  const weekday = short >= 0 ? short : long;
  if (weekday >= 0) {
    const delta = (weekday - weekdayOf(today) + 7) % 7 || 7;
    return { day: today + delta, count: 1 };
  }
  const second = words[i + 1]?.text.toLowerCase();
  if (lower === W.nextWeek[0] && second === W.nextWeek[1]) {
    return { day: today + (7 - weekdayOf(today)), count: 2 };
  }
  if (lower === W.inDays.prefix && second !== undefined && /^\d{1,3}$/.test(second)) {
    const unit = words[i + 2]?.text.toLowerCase();
    if (unit !== undefined && W.inDays.units.includes(unit)) return { day: today + Number(second), count: 3 };
  }
  const numeric = numericDate(word, today);
  if (numeric !== null) return { day: numeric, count: 1 };
  return null;
}

function firstName(displayName: string): string {
  return (displayName.trim().split(/\s+/)[0] ?? '').toLowerCase();
}

function matchPerson(q: string, people: readonly QuickEntryPerson[]): QuickEntryPerson[] {
  const query = q.toLowerCase();
  const exact = people.filter((p) => firstName(p.displayName) === query || p.initials.toLowerCase() === query);
  if (exact.length > 0) return exact;
  return people.filter((p) => firstName(p.displayName).startsWith(query));
}

function matchProject(q: string, projects: readonly QuickEntryNamed[]): QuickEntryNamed[] {
  const query = q.toLowerCase();
  const exact = projects.filter((p) => p.name.trim().toLowerCase() === query);
  if (exact.length > 0) return exact;
  return projects.filter((p) => p.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(query)));
}

const squash = (s: string) => s.toLowerCase().replace(/\s+/g, '');

function pick<T>(found: T[], text: string): QuickEntryPick<T> | null {
  if (found.length === 0) return null;
  return found.length === 1 ? found[0]! : { ambiguous: found, text };
}

/** Satz der Schnell-Erfassung lesen (siehe Kopf). */
export function parseTaskQuickEntry(text: string, today: string, directory: QuickEntryDirectory, options: QuickEntryOptions = {}): QuickEntryResult {
  if (!isIsoDate(today)) throw new RangeError(`Kein gültiges Datum für heute: ${today}`);
  const W = TASK_QUICK_ENTRY_WORDS;
  const ignore = options.ignore ?? [];
  const ignored = (kind: QuickEntryKind, word: string) =>
    ignore.some((x) => (typeof x === 'string' ? x === kind : x.kind === kind && x.text === word));
  const todayDay = dayNumber(today);
  const words: Word[] = [];
  for (const m of text.matchAll(/\S+/g)) words.push({ text: m[0], start: m.index, end: m.index + m[0].length });

  const result: QuickEntryResult = { version: TASK_QUICK_ENTRY_VERSION, title: '', dueDate: null, priority: null, person: null, labels: [], project: null, record: null, tokens: [] };
  const consumed = new Set<number>();
  const mark = (kind: QuickEntryKind, from: number, count: number, remove: boolean) => {
    const first = words[from]!;
    const last = words[from + count - 1]!;
    result.tokens.push({ kind, start: first.start, end: last.end, text: text.slice(first.start, last.end) });
    if (remove) for (let k = from; k < from + count; k += 1) consumed.add(k);
  };

  for (let i = 0; i < words.length; i += 1) {
    const word = words[i]!.text;
    const lower = word.toLowerCase();

    if (result.dueDate === null) {
      const date = dateAt(words, i, todayDay);
      if (date) {
        const phrase = text.slice(words[i]!.start, words[i + date.count - 1]!.end);
        if (!ignored('due', phrase)) {
          result.dueDate = isoOf(date.day);
          mark('due', i, date.count, true);
          i += date.count - 1;
          continue;
        }
      }
    }

    const priority = own(W.priorities, lower);
    if (priority !== undefined) {
      if (result.priority === null && !ignored('priority', word)) {
        result.priority = priority;
        mark('priority', i, 1, true);
      }
      continue;
    }

    if (word.startsWith(W.person) && word.length > W.person.length) {
      if (result.person === null && !ignored('person', word)) {
        const found = pick(matchPerson(word.slice(W.person.length), directory.people), word);
        if (found) {
          result.person = found;
          mark('person', i, 1, true);
        }
      }
      continue;
    }

    if (word.startsWith(W.label) && word.length > W.label.length) {
      const q = squash(word.slice(W.label.length));
      const label = directory.labels.find((l) => squash(l.name) === q);
      if (label && !ignored('label', word)) {
        if (!result.labels.some((l) => l.id === label.id)) result.labels.push(label);
        mark('label', i, 1, true);
      }
      continue;
    }

    if (word.startsWith(W.project) && word.length > W.project.length) {
      if (result.project === null && !ignored('project', word)) {
        const found = pick(matchProject(word.slice(W.project.length), directory.projects), word);
        if (found) {
          result.project = found;
          mark('project', i, 1, true);
        }
      }
      continue;
    }

    const recordKind = own(W.records, lower);
    const next = words[i + 1]?.text;
    if (recordKind !== undefined && next !== undefined && result.record === null && options.records !== false) {
      const fits = recordKind === 'article' ? ARTICLE_NUMBER.test(next) && /\d/.test(next) : CUSTOMER_OR_ORDER_NUMBER.test(next);
      if (fits && !ignored('record', text.slice(words[i]!.start, words[i + 1]!.end))) {
        result.record = { kind: recordKind, number: next };
        mark('record', i, 2, false);
        i += 1;
      }
    }
  }

  result.title = words
    .filter((_, k) => !consumed.has(k))
    .map((w) => w.text)
    .join(' ');
  result.tokens.sort((a, b) => a.start - b.start);
  return result;
}

// ---------------------------------------------------------------------------
// Story 23.15: KI-Deutung als Vorschlag – Zusammenführen von Regel-Deutung und KI-Antwort (rein, AD-27).

export const TASK_QUICK_ENTRY_AI_VERSION = 'task-quick-entry-ai-v1';

/**
 * Was die feste Deutung (`parseTaskQuickEntry`) plus die Chip-Änderungen der Person schon festgelegt haben. `null` bzw.
 * leere Liste = offen; nur dort darf die KI etwas eintragen.
 */
export interface QuickEntryFixed {
  dueDate: string | null;
  priority: QuickEntryPriority | null;
  personId: string | null;
  labelIds: readonly string[];
  projectId: string | null;
}

/** Antwort der KI (schon gegen das Schema geprüft; Kennungen und Datum prüft erst `mergeQuickEntryAi`). */
export interface QuickEntryAiAnswer {
  title: string | null;
  dueDate: string | null;
  priority: QuickEntryPriority | null;
  personId: string | null;
  labelIds: readonly string[];
  projectId: string | null;
}

export type QuickEntryAiField = 'title' | 'dueDate' | 'priority' | 'personId' | 'labelIds' | 'projectId';

/**
 * Warum ein Feld der KI nicht gilt: `fixed` Regel/Person hat es schon · `unknown_id` Kennung nicht im mitgegebenen
 * Verzeichnis · `invalid_date` kein gültiges Datum · `past_date` vor heute · `empty_title` kein Titel.
 */
export type QuickEntryAiDropReason = 'fixed' | 'unknown_id' | 'invalid_date' | 'past_date' | 'empty_title';

export interface QuickEntryAiMerge {
  version: typeof TASK_QUICK_ENTRY_AI_VERSION;
  /** `false` = kein brauchbarer Vorschlag (kein Titel von der KI) – nichts anlegen, die Karte sagt es ruhig. */
  usable: boolean;
  title: string | null;
  dueDate: string | null;
  priority: QuickEntryPriority | null;
  personId: string | null;
  labelIds: string[];
  projectId: string | null;
  /** Felder, die aus der KI stammen (Rest aus der Regel oder leer). */
  fromAi: QuickEntryAiField[];
  dropped: { field: QuickEntryAiField; reason: QuickEntryAiDropReason }[];
}

const QUICK_ENTRY_PRIORITIES: readonly QuickEntryPriority[] = ['urgent', 'high', 'normal', 'low'];

/**
 * Regel-Deutung und KI-Antwort zusammenführen (Story 23.15). Regeln:
 * - Was `fixed` festlegt, gilt immer; die KI füllt nur leere Felder (Labels nur, wenn die Regel keine erkannt hat).
 * - Titel kommt immer von der KI (getrimmt); leer → nicht brauchbar.
 * - Kennungen nur aus dem mitgegebenen Verzeichnis (Personen, Labels, aktive Projekte); fremde → Feld verworfen.
 * - Datum nur gültig (`YYYY-MM-DD`) und nicht vor `today`; nie verschoben (Wochenende/Feiertag bleibt).
 * - Ein verworfenes Feld lässt den Rest stehen. Datensätze (Kunde, Auftrag, Artikel) kennt die KI nicht – nur die Regel.
 */
export function mergeQuickEntryAi(fixed: QuickEntryFixed, ai: QuickEntryAiAnswer, directory: QuickEntryDirectory, today: string): QuickEntryAiMerge {
  const fromAi: QuickEntryAiField[] = [];
  const dropped: QuickEntryAiMerge['dropped'] = [];
  const drop = (field: QuickEntryAiField, reason: QuickEntryAiDropReason) => dropped.push({ field, reason });

  const aiTitle = (ai.title ?? '').replace(/\s+/g, ' ').trim();
  const title = aiTitle === '' ? null : aiTitle;
  if (title === null) drop('title', 'empty_title');
  else fromAi.push('title');

  let dueDate = fixed.dueDate;
  if (ai.dueDate !== null) {
    if (fixed.dueDate !== null) {
      if (ai.dueDate !== fixed.dueDate) drop('dueDate', 'fixed');
    } else if (!isIsoDate(ai.dueDate)) drop('dueDate', 'invalid_date');
    else if (ai.dueDate < today) drop('dueDate', 'past_date');
    else {
      dueDate = ai.dueDate;
      fromAi.push('dueDate');
    }
  }

  let priority = fixed.priority;
  if (ai.priority !== null) {
    if (fixed.priority !== null) {
      if (ai.priority !== fixed.priority) drop('priority', 'fixed');
    } else if (!QUICK_ENTRY_PRIORITIES.includes(ai.priority)) drop('priority', 'unknown_id');
    else {
      priority = ai.priority;
      fromAi.push('priority');
    }
  }

  const pick = (field: 'personId' | 'projectId', fixedValue: string | null, aiValue: string | null, known: readonly { id: string }[]): string | null => {
    if (aiValue === null) return fixedValue;
    if (fixedValue !== null) {
      if (aiValue !== fixedValue) drop(field, 'fixed');
      return fixedValue;
    }
    if (!known.some((k) => k.id === aiValue)) {
      drop(field, 'unknown_id');
      return null;
    }
    fromAi.push(field);
    return aiValue;
  };
  const personId = pick('personId', fixed.personId, ai.personId, directory.people);
  const projectId = pick('projectId', fixed.projectId, ai.projectId, directory.projects);

  let labelIds = [...fixed.labelIds];
  if (ai.labelIds.length > 0) {
    if (fixed.labelIds.length > 0) {
      if (ai.labelIds.some((id) => !fixed.labelIds.includes(id))) drop('labelIds', 'fixed');
    } else {
      const known = new Set(directory.labels.map((l) => l.id));
      const valid = [...new Set(ai.labelIds)].filter((id) => known.has(id));
      if (valid.length < new Set(ai.labelIds).size) drop('labelIds', 'unknown_id');
      if (valid.length > 0) {
        labelIds = valid;
        fromAi.push('labelIds');
      }
    }
  }

  return { version: TASK_QUICK_ENTRY_AI_VERSION, usable: title !== null, title, dueDate, priority, personId, labelIds, projectId, fromAi, dropped };
}
